import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Skeleton,
  Alert,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import Can from '../../../../components/Can';
import OnGeneralMessageDialog from '../../../../components/Dialogs/OnGeneralMessageDialog';
import AsignarCargoDialog from '../options/asignarcargodialog';
import { fetchCargosAsignados, eliminarCargoAsignado } from '../../../../utils/generalsFetch';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'warning' },
  parcial: { label: 'Parcial', color: 'info' },
  pagado: { label: 'Pagado', color: 'success' },
  anulado: { label: 'Anulado', color: 'default' },
};

export default function CargosAsignadosTab({ prestamo }) {
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAsignar, setOpenAsignar] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    if (!prestamo?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchCargosAsignados({
        idPrestamo: prestamo.id,
        idcompany: localStorage.getItem('idcompany'),
        usuarioId: localStorage.getItem('id'),
      });
      setCargos(data);
    } catch (e) {
      setError(e.message || 'Error cargando cargos asignados');
    } finally {
      setLoading(false);
    }
  }, [prestamo?.id]);

  useEffect(() => { load(); }, [load]);

  const handleConfirmDelete = async () => {
    if (!confirmDel) return;
    try {
      await eliminarCargoAsignado({
        id: confirmDel.id,
        idcompany: localStorage.getItem('idcompany'),
        usuarioId: localStorage.getItem('id'),
      });
      setConfirmDel(null);
      load();
    } catch (e) {
      setError(e.message || 'Error al eliminar el cargo');
      setConfirmDel(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        
        <Can modulo="categorias_cargos" accion="crear">
          <Button
            variant="contained"
            color="primarybutton"
            startIcon={<Add />}
            sx={{ fontWeight: 700, marginTop:4 }}
            onClick={() => setOpenAsignar(true)}
          >
            Asignar cargo
          </Button>
        </Can>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {cargos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No hay cargos asignados a este préstamo.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledCellHeader>Categoría</StyledCellHeader>
                <StyledCellHeader align="right">Monto</StyledCellHeader>
                <StyledCellHeader align="right">Pagado</StyledCellHeader>
                <StyledCellHeader align="right">Saldo</StyledCellHeader>
                <StyledCellHeader>Estado</StyledCellHeader>
                <StyledCellHeader>Descripción</StyledCellHeader>
                <StyledCellHeader align="center">Acciones</StyledCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {cargos.map((c) => {
                const estado = ESTADOS[c.estado] || ESTADOS.pendiente;
                const sinPagos = parseFloat(c.monto_pagado) === 0;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.categoria_nombre}</TableCell>
                    <TableCell align="right">RD$ {formatNumberWithCommas(parseFloat(c.monto_total).toFixed(2))}</TableCell>
                    <TableCell align="right">RD$ {formatNumberWithCommas(parseFloat(c.monto_pagado).toFixed(2))}</TableCell>
                    <TableCell align="right">
                      <strong>RD$ {formatNumberWithCommas(parseFloat(c.saldo).toFixed(2))}</strong>
                    </TableCell>
                    <TableCell><Chip size="small" color={estado.color} label={estado.label} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{c.descripcion || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Can modulo="categorias_cargos" accion="eliminar">
                        <Tooltip title={sinPagos ? 'Eliminar' : 'No se puede eliminar (tiene pagos)'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!sinPagos}
                              onClick={() => setConfirmDel(c)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Can>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AsignarCargoDialog
        open={openAsignar}
        onClose={() => setOpenAsignar(false)}
        onSuccess={load}
        prestamo={prestamo}
      />

      <OnGeneralMessageDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onRegister={handleConfirmDelete}
        title="Eliminar cargo asignado"
        message={`¿Eliminar el cargo "${confirmDel?.categoria_nombre}"? Esta acción no se puede deshacer.`}
        buttonText="Eliminar"
        buttonColor="error"
      />
    </Box>
  );
}
